import React, { useState, useMemo } from "react";
import Papa from "papaparse";
import { useApp } from "../context/AppContext";
import api from "../lib/api";
import { Button, Modal, Select } from "./ui";
import { AccountSelect } from "./ActivityForms";
import { Upload, FileSpreadsheet, AlertTriangle, Check, Download, ChevronDown, ChevronRight } from "lucide-react";

const FIELDS_CORE = ["date", "type", "symbol", "name", "quantity", "price", "fee", "amount", "currency"];
const FIELDS_ADV = ["asset_type", "option_side", "strike", "expiration", "coupon", "description"];
const ALL_FIELDS = [...FIELDS_CORE, ...FIELDS_ADV];

// header keyword → field (lowercased, substring match) for auto-detect
const HEADER_HINTS = {
  date: ["date", "trade date", "settlement"],
  type: ["type", "operation", "transaction", "sens", "side", "action", "nature"],
  symbol: ["symbol", "ticker", "isin", "code", "mnemo"],
  name: ["name", "label", "libell", "description", "nom", "instrument", "designation", "produit", "product"],
  quantity: ["quantity", "qty", "quantit", "shares", "nombre", "nb"],
  price: ["price", "cours", "prix", "unit", "pru", "share price"],
  fee: ["fee", "frais", "commission", "courtage"],
  amount: ["amount", "montant", "total", "net"],
  currency: ["currency", "devise", "ccy", "monnaie"],
  asset_type: ["asset type", "asset_type", "instrument type", "categorie", "category", "class"],
  option_side: ["call/put", "option side", "side", "sens option"],
  strike: ["strike", "prix d'exercice"],
  expiration: ["expiration", "expiry", "echeance", "maturity", "maturité", "échéance"],
  coupon: ["coupon", "taux", "rate"],
  description: ["description", "libell", "operation", "opération", "nature", "narrative", "mouvement"],
};

const TYPE_SYNONYMS = {
  buy: ["buy", "achat", "purchase", "bought", "b", "acquisition"],
  sell: ["sell", "vente", "sold", "sale", "s", "cession"],
  deposit: ["deposit", "depot", "dépôt", "versement", "funding", "credit"],
  withdrawal: ["withdrawal", "retrait", "withdraw", "debit"],
  dividend: ["dividend", "dividende", "div", "coupon"],
  transfer: ["transfer", "virement", "transfert"],
  split: ["split", "division", "fractionnement"],
  fee: ["fee", "frais", "commission", "courtage"],
  interest: ["interest", "interet", "intérêt", "interets", "intérêts"],
  tax: ["tax", "impot", "impôt", "taxe", "prelevement", "prélèvement"],
};

const ASSET_SYNONYMS = {
  stock: ["stock", "action", "equity", "share", "aandeel"],
  etf: ["etf", "tracker"],
  fund: ["fund", "fonds", "sicav", "oeic", "mutual"],
  option: ["option", "call", "put", "warrant"],
  bond: ["bond", "obligation", "oblig", "oat", "treasury", "gilt"],
};

const SIDE_SYNONYMS = { call: ["call", "c"], put: ["put", "p"] };

// classify a free-text cash-book description into an activity type (FR/EN/NL).
// Used by cash-book exports (Degiro Account.csv, Boursorama movements) where the
// type lives in a Description column rather than a dedicated type column.
const DESC_SYNONYMS = {
  tax: ["impôt", "impot", "taxe", "précompte", "precompte", "withholding", "prélèvement", "prelevement"],
  fee: ["frais", "commission", "courtage", "connexion", "kosten", "exchange connection"],
  interest: ["intérêt", "interet", "interest", "rente"],
  dividend: ["dividend", "dividende", "coupon"],
  deposit: ["versement", "dépôt", "depot", "deposit", "réception", "reception", "storting", "approvisionnement"],
  withdrawal: ["retrait", "withdrawal", "withdraw", "terugstorting", "virement sortant"],
  buy: ["achat", "koop", " buy "],
  sell: ["vente", "verkoop", " sell "],
};

function classifyDescription(raw) {
  const v = ` ${(raw || "").trim().toLowerCase()} `;
  if (v.trim() === "") return null;
  for (const [canon, syns] of Object.entries(DESC_SYNONYMS)) {
    if (syns.some((s) => v.includes(s))) return canon;
  }
  return null;
}

// broker presets: candidate header names per field + options.
// typeFromQuantitySign: derive buy/sell from the sign of the quantity column.
const BROKER_PRESETS = {
  degiro: {
    label: "Degiro",
    headers: {
      date: ["date"], name: ["produit", "product"], symbol: ["isin"],
      quantity: ["quantité", "quantity", "quantite", "aantal"],
      price: ["cours", "price", "koers"],
      fee: ["frais de transaction", "transaction costs", "frais", "transactiekosten"],
    },
    typeFromQuantitySign: true,
  },
  boursorama: {
    label: "Boursorama",
    headers: {
      date: ["date"], name: ["libellé", "libelle", "label"],
      symbol: ["isin", "code", "symbole"],
      type: ["sens", "type", "operation", "opération", "nature"],
      quantity: ["quantité", "quantite", "qty"],
      price: ["cours", "prix", "price"], amount: ["montant", "amount"],
    },
  },
  traderepublic: {
    label: "Trade Republic",
    headers: {
      date: ["date"], type: ["type", "transaction"],
      symbol: ["isin", "symbol", "ticker"],
      name: ["name", "instrument", "description"],
      quantity: ["shares", "quantity", "qty", "nombre"],
      price: ["price", "share price"], amount: ["amount", "total", "value"],
    },
  },
  cashbook: {
    label: "Cash book (Degiro / Boursorama)",
    headers: {
      date: ["date"], name: ["produit", "product", "libellé", "libelle"], symbol: ["isin"],
      description: ["description", "libellé", "libelle", "operation", "opération", "nature", "mouvement", "label"],
      amount: ["mouvements", "montant", "amount", "value", "credit", "débit"],
      currency: ["devise", "currency", "monnaie", "ccy"],
    },
    typeFromDescription: true,
  },
};

function normSynonym(raw, table) {
  const v = (raw || "").trim().toLowerCase();
  if (!v) return null;
  for (const [canon, syns] of Object.entries(table)) {
    // exact match any length; prefix match only for synonyms >= 3 chars
    if (syns.some((s) => v === s || (s.length >= 3 && v.startsWith(s)))) return canon;
  }
  return null;
}
const normType = (raw) => normSynonym(raw, TYPE_SYNONYMS);
const normAssetType = (raw) => normSynonym(raw, ASSET_SYNONYMS);
const normSide = (raw) => normSynonym(raw, SIDE_SYNONYMS);

function normDate(raw) {
  const s = (raw || "").trim();
  if (!s) return null;
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
  const dm = s.match(/^(\d{1,2})[-/.](\d{1,2})[-/.](\d{4})$/); // DD/MM/YYYY or DD-MM-YYYY (FR brokers)
  if (dm) {
    const [, d, m, y] = dm;
    return `${y}-${m.padStart(2, "0")}-${d.padStart(2, "0")}`;
  }
  const dt = new Date(s);
  return Number.isNaN(dt.getTime()) ? null : dt.toISOString().slice(0, 10);
}

function normNum(raw) {
  if (raw == null || raw === "") return null;
  let s = String(raw).replace(/\s/g, "").replace(/[^\d.,-]/g, "");
  if (s.includes(",") && s.includes(".")) s = s.replace(/,/g, ""); // 1,234.56
  else if (s.includes(",")) s = s.replace(",", "."); // 5,69
  const n = Number(s);
  return Number.isNaN(n) ? null : n;
}

// header auto-detection (generic) using HEADER_HINTS substrings
function autoMap(headers) {
  const map = {};
  ALL_FIELDS.forEach((f) => {
    const found = headers.find((h) => {
      const hl = (h || "").toLowerCase();
      return (HEADER_HINTS[f] || []).some((k) => hl.includes(k));
    });
    map[f] = found || "";
  });
  return map;
}

// apply a broker preset's candidate header names (exact then substring)
function presetMap(headers, preset) {
  const map = {};
  ALL_FIELDS.forEach((f) => {
    const cands = preset.headers[f] || [];
    let found = "";
    for (const c of cands) {
      const m = headers.find((h) => (h || "").toLowerCase().trim() === c);
      if (m) { found = m; break; }
    }
    if (!found) {
      for (const c of cands) {
        const m = headers.find((h) => (h || "").toLowerCase().includes(c));
        if (m) { found = m; break; }
      }
    }
    map[f] = found;
  });
  return map;
}

function buildMapping(headers, presetKey) {
  const preset = BROKER_PRESETS[presetKey];
  if (!preset) return { mapping: autoMap(headers), typeFromSign: false, typeFromDesc: false };
  const mapping = presetMap(headers, preset);
  return {
    mapping,
    typeFromSign: !!preset.typeFromQuantitySign && !mapping.type,
    typeFromDesc: !!preset.typeFromDescription && !mapping.type,
  };
}

export default function ImportActivitiesModal({ entity, accounts, defaultAccountId, onClose, onImported }) {
  const { t, toast } = useApp();
  const [presetKey, setPresetKey] = useState("generic");
  const [headers, setHeaders] = useState([]);
  const [rawRows, setRawRows] = useState([]);
  const [mapping, setMapping] = useState({});
  const [typeFromSign, setTypeFromSign] = useState(false);
  const [typeFromDesc, setTypeFromDesc] = useState(false);
  const [account, setAccount] = useState(defaultAccountId || accounts[0]?.id || "");
  const [busy, setBusy] = useState(false);
  const [fileName, setFileName] = useState("");
  const [showAdv, setShowAdv] = useState(false);

  const applyMapping = (flds, key) => {
    const { mapping: m, typeFromSign: tfs, typeFromDesc: tfd } = buildMapping(flds, key);
    setMapping(m);
    setTypeFromSign(tfs);
    setTypeFromDesc(tfd);
  };

  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileName(file.name);
    Papa.parse(file, {
      header: true, skipEmptyLines: true,
      complete: (res) => {
        const flds = res.meta.fields || [];
        setHeaders(flds);
        setRawRows(res.data || []);
        applyMapping(flds, presetKey);
      },
    });
  };

  const onPresetChange = (key) => {
    setPresetKey(key);
    if (headers.length) applyMapping(headers, key);
  };

  const parsed = useMemo(() => rawRows.map((row) => {
    const get = (f) => (mapping[f] ? row[mapping[f]] : "");
    const rawQty = normNum(get("quantity"));
    let type;
    let quantity;
    if (typeFromSign && !mapping.type) {
      type = rawQty > 0 ? "buy" : rawQty < 0 ? "sell" : null;
      quantity = rawQty == null ? null : Math.abs(rawQty);
    } else if (typeFromDesc && !mapping.type) {
      type = classifyDescription(get("description"));
      quantity = rawQty;
    } else {
      type = normType(get("type"));
      quantity = rawQty;
    }
    const date = normDate(get("date"));
    const at = normAssetType(get("asset_type")) || "stock";
    const isOpt = at === "option";
    const isBond = at === "bond";
    return {
      type, date,
      symbol: (get("symbol") || "").trim() || null,
      name: (get("name") || "").trim() || null,
      asset_type: at,
      quantity,
      price: normNum(get("price")),
      fee: normNum(get("fee")),
      amount: normNum(get("amount")),
      currency: (get("currency") || "").trim().toUpperCase() || null,
      option_side: isOpt ? normSide(get("option_side")) : null,
      strike: isOpt ? normNum(get("strike")) : null,
      expiration: (isOpt || isBond) ? normDate(get("expiration")) : null,
      coupon: isBond ? normNum(get("coupon")) : null,
      valid: !!(type && date),
    };
  }), [rawRows, mapping, typeFromSign, typeFromDesc]);

  const validRows = parsed.filter((r) => r.valid);
  const invalidCount = parsed.length - validRows.length;

  const doImport = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/portfolio/activities/import", {
        entity_id: entity.id,
        account_id: account || null,
        rows: validRows.map(({ valid, ...r }) => r),
      });
      toast(t("import_done").replace("{n}", data.imported).replace("{s}", data.skipped));
      onImported?.();
    } catch {
      toast(t("import_error"));
    } finally {
      setBusy(false);
    }
  };

  const downloadTemplate = () => {
    const csv = "date,type,symbol,name,asset_type,quantity,price,fee,amount,currency\n"
      + "2026-01-15,buy,AAPL,Apple,stock,10,180.50,1.5,,USD\n"
      + "2026-02-01,dividend,AAPL,Apple,stock,,,,12.40,USD\n"
      + "2026-03-10,sell,IE00B4L5Y983,iShares Core MSCI World,etf,5,95.00,1.5,,EUR\n";
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "activities_template.csv";
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const PresetSelect = (
    <div>
      <label className="text-xs text-text-secondary block mb-1">{t("import_preset")}</label>
      <Select value={presetKey} onChange={(e) => onPresetChange(e.target.value)} data-testid="import-preset-select">
        <option value="generic">{t("import_preset_auto")}</option>
        {Object.entries(BROKER_PRESETS).map(([k, p]) => <option key={k} value={k}>{p.label}</option>)}
      </Select>
    </div>
  );

  return (
    <Modal open onClose={onClose} title={t("import_title")}
      footer={<>
        <Button variant="outline" onClick={onClose} data-testid="import-cancel">{t("cancel")}</Button>
        <Button onClick={doImport} disabled={busy || validRows.length === 0} data-testid="import-confirm">
          {busy ? t("loading") : `${t("import_confirm")} (${validRows.length})`}
        </Button>
      </>}>
      <div className="space-y-4">
        {rawRows.length === 0 ? (
          <div className="space-y-3">
            <p className="text-sm text-text-secondary">{t("import_intro")}</p>
            {PresetSelect}
            <label className="flex flex-col items-center justify-center gap-2 border-2 border-dashed rounded-xl py-8 cursor-pointer hover:bg-surface-hover transition-colors" data-testid="import-dropzone">
              <Upload className="w-6 h-6 text-text-secondary" />
              <span className="text-sm text-text-secondary">{t("import_choose_file")}</span>
              <input type="file" accept=".csv,text/csv" className="hidden" onChange={onFile} data-testid="import-file-input" />
            </label>
            <button onClick={downloadTemplate} className="text-xs text-text-secondary hover:text-text-primary inline-flex items-center gap-1" data-testid="import-template">
              <Download className="w-3.5 h-3.5" /> {t("import_template")}
            </button>
          </div>
        ) : (
          <>
            <div className="flex items-center gap-2 text-sm">
              <FileSpreadsheet className="w-4 h-4 text-text-secondary" />
              <span className="font-medium">{fileName}</span>
              <span className="text-text-secondary">· {rawRows.length} {t("import_rows")}</span>
            </div>

            <div className="grid grid-cols-2 gap-3">
              {PresetSelect}
              <div>
                <label className="text-xs text-text-secondary block mb-1">{t("portfolio_account")}</label>
                <AccountSelect accounts={accounts} value={account} onChange={setAccount} testid="import-account-select" />
              </div>
            </div>

            {typeFromSign && (
              <p className="text-[11px] text-text-secondary" data-testid="import-type-from-sign">{t("import_type_from_sign")}</p>
            )}
            {typeFromDesc && (
              <p className="text-[11px] text-text-secondary" data-testid="import-type-from-desc">{t("import_type_from_desc")}</p>
            )}

            {/* column mapping */}
            <div>
              <div className="text-xs font-medium text-text-secondary mb-2">{t("import_mapping")}</div>
              <div className="grid grid-cols-3 gap-2" data-testid="import-mapping">
                {FIELDS_CORE.map((f) => (
                  <MapSelect key={f} f={f} t={t} headers={headers} mapping={mapping} setMapping={setMapping} />
                ))}
              </div>
              <button onClick={() => setShowAdv((s) => !s)} data-testid="import-advanced-toggle"
                className="mt-2 text-[11px] text-text-secondary hover:text-text-primary inline-flex items-center gap-1">
                {showAdv ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                {t("import_advanced")}
              </button>
              {showAdv && (
                <div className="grid grid-cols-3 gap-2 mt-2" data-testid="import-mapping-advanced">
                  {FIELDS_ADV.map((f) => (
                    <MapSelect key={f} f={f} t={t} headers={headers} mapping={mapping} setMapping={setMapping} />
                  ))}
                </div>
              )}
            </div>

            {/* counts */}
            <div className="flex items-center gap-4 text-xs">
              <span className="inline-flex items-center gap-1" style={{ color: "#10B981" }}><Check className="w-3.5 h-3.5" /> {validRows.length} {t("import_valid")}</span>
              {invalidCount > 0 && (
                <span className="inline-flex items-center gap-1 text-amber-500" data-testid="import-invalid-count"><AlertTriangle className="w-3.5 h-3.5" /> {invalidCount} {t("import_skipped")}</span>
              )}
            </div>

            {/* preview */}
            <div className="max-h-44 overflow-auto rounded-lg border" data-testid="import-preview">
              <table className="w-full text-xs">
                <thead className="sticky top-0 bg-surface">
                  <tr className="text-left text-text-secondary border-b">
                    <th className="py-1.5 px-2">{t("import_field_date")}</th>
                    <th className="py-1.5 px-2">{t("import_field_type")}</th>
                    <th className="py-1.5 px-2">{t("import_field_symbol")}</th>
                    <th className="py-1.5 px-2">{t("import_field_asset_type")}</th>
                    <th className="py-1.5 px-2 text-right">{t("import_field_quantity")}</th>
                    <th className="py-1.5 px-2 text-right">{t("import_field_price")}</th>
                    <th className="py-1.5 px-2 text-right">{t("import_field_amount")}</th>
                  </tr>
                </thead>
                <tbody>
                  {parsed.slice(0, 50).map((r, i) => (
                    <tr key={i} className={`border-b last:border-0 ${r.valid ? "" : "opacity-40"}`}>
                      <td className="py-1 px-2 tabular-nums">{r.date || "—"}</td>
                      <td className="py-1 px-2">{r.type || <span className="text-amber-500">?</span>}</td>
                      <td className="py-1 px-2 font-mono">{r.symbol || "—"}</td>
                      <td className="py-1 px-2">{r.asset_type}</td>
                      <td className="py-1 px-2 text-right tabular-nums">{r.quantity ?? "—"}</td>
                      <td className="py-1 px-2 text-right tabular-nums">{r.price ?? "—"}</td>
                      <td className="py-1 px-2 text-right tabular-nums">{r.amount ?? "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}

function MapSelect({ f, t, headers, mapping, setMapping }) {
  return (
    <div>
      <label className="text-[11px] text-text-secondary block mb-0.5">{t(`import_field_${f}`)}</label>
      <Select value={mapping[f] || ""} onChange={(e) => setMapping((m) => ({ ...m, [f]: e.target.value }))}
        data-testid={`import-map-${f}`} className="text-xs">
        <option value="">—</option>
        {headers.map((h) => <option key={h} value={h}>{h}</option>)}
      </Select>
    </div>
  );
}
